import { useEffect, useMemo } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { PUMP_LIST, type PumpId } from "@tbm/shared";
import { api } from "../lib/api";
import { usePreprocessingParams } from "../state/preprocessing";
import { PlotCard } from "../components/PlotCard";
import { useTimezone, utcToLocalNaive } from "../lib/datetime";
import { usePersistentState } from "../lib/usePersistentState";

type Range = "1h" | "8h" | "24h" | "7d" | "drive";

const RANGES: { id: Range; label: string }[] = [
  { id: "1h",    label: "1h" },
  { id: "8h",    label: "8h" },
  { id: "24h",   label: "24h" },
  { id: "7d",    label: "7d" },
  { id: "drive", label: "Drive" },
];

const RELATIVE: Record<Exclude<Range, "drive">, string> = {
  "1h":  "-1h",
  "8h":  "-8h",
  "24h": "-24h",
  "7d":  "-7d",
};

// Trace colours match the old PumpData.js Plotly default colourway: blue,
// orange, green, red — same order as the old legend.
const TRACE_COLORS: Record<string, string> = {
  current:     "#1f77b4",
  revs:        "#ff7f0e",
  utilisation: "#2ca02c",
  power:       "#d62728",
};

export function Pumps() {
  const [range, setRange] = usePersistentState<Range>("pumps.range", "7d");
  const pp = usePreprocessingParams();

  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const availability = useQuery({
    queryKey: ["pumps", "availability"],
    queryFn: api.pumpAvailability,
    refetchInterval: 60_000,
  });

  const availableSet = useMemo(() => {
    const s = new Set<PumpId>();
    for (const p of availability.data?.pumps ?? []) {
      if (p.hasData) s.add(p.id);
    }
    return s;
  }, [availability.data]);

  // Default-select the first pump that has data.
  const [pumpId, setPumpId] = usePersistentState<PumpId>(
    "pumps.pumpId",
    PUMP_LIST[0]!.id,
  );
  useEffect(() => {
    if (availableSet.size === 0) return;
    if (!availableSet.has(pumpId)) {
      const first = PUMP_LIST.find((p) => availableSet.has(p.id));
      if (first) setPumpId(first.id);
    }
  }, [availableSet, pumpId]);

  const from = range === "drive" ? drive.data?.startDate : RELATIVE[range];

  const seriesQuery = useQuery({
    queryKey: ["pumps", "series", pumpId, range, from, pp],
    queryFn: () =>
      api.pumpSeries({ pump: pumpId, from: from!, preprocessing: pp }),
    enabled: !!from,
    refetchInterval: 30_000,
  });

  const tz = useTimezone();

  const traces = (seriesQuery.data?.traces ?? []).map((t) => ({
    x: t.points.map((p) => utcToLocalNaive(p.t, tz)),
    y: t.points.map((p) => p.v),
    type: "scatter",
    mode: "lines",
    name: `${t.label} (${t.unit})`,
    line: { color: TRACE_COLORS[t.metric] ?? "#1f77b4", width: 1.4 },
    connectgaps: false,
    hovertemplate: `${t.label}: %{y:.2f} ${t.unit}<extra></extra>`,
  })) as Record<string, unknown>[];

  const totalPoints = (seriesQuery.data?.traces ?? []).reduce(
    (n, t) => n + t.points.length,
    0,
  );

  const subtitle = seriesQuery.isLoading
    ? "loading…"
    : totalPoints === 0
      ? "no data in window"
      : `${PUMP_LIST.find((p) => p.id === pumpId)?.displayName ?? ""}`;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between pt-1">
        <div>
          <h2 className="text-[13px] font-semibold tracking-[0.18em] uppercase text-text">
            Pumps
          </h2>
          <div className="text-[11px] text-muted mt-1">
            Output current, frequency, utilisation, and power for each slurry pump.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <PumpSelect
          value={pumpId}
          onChange={setPumpId}
          availability={availability.data?.pumps ?? []}
          isLoading={availability.isLoading}
        />
        <div className="flex-1" />
        <RangeToggle range={range} onChange={setRange} />
      </div>

      <PlotCard
        title={PUMP_LIST.find((p) => p.id === pumpId)?.displayName ?? ""}
        subtitle={subtitle}
        data={traces as never}
        height={420}
        // See DriveParameters.tsx — range is part of the revision so a
        // click on 1h / 8h / 24h / 7d / Drive forces autorange.
        uirevision={`pump-${pumpId}-${range}`}
        layout={{
          margin: { l: 56, r: 16, t: 64, b: 36 },
          xaxis: {
            tickformat: "%-d %b",
            hoverformat: "%-d %b %Y · %H:%M",
          },
          showlegend: true,
          legend: {
            x: 0.99,
            y: 0.99,
            xanchor: "right",
            yanchor: "top",
            bgcolor: "rgba(255,255,255,0.85)",
            bordercolor: "rgba(0,0,0,0.08)",
            borderwidth: 1,
            font: { size: 10, color: "#374151" },
          },
        }}
      />
    </div>
  );
}

function PumpSelect({
  value,
  onChange,
  availability,
  isLoading,
}: {
  value: PumpId;
  onChange: (v: PumpId) => void;
  availability: Array<{ id: PumpId; displayName: string; hasData: boolean }>;
  isLoading: boolean;
}) {
  // Build a quick lookup of which pumps have data; if availability hasn't
  // loaded yet, treat all as available so the dropdown stays usable.
  const availableSet = useMemo(() => {
    const s = new Set<PumpId>();
    if (isLoading || availability.length === 0) {
      for (const p of PUMP_LIST) s.add(p.id);
      return s;
    }
    for (const a of availability) {
      if (a.hasData) s.add(a.id);
    }
    return s;
  }, [availability, isLoading]);

  return (
    <label className="inline-flex items-center bg-surface2 border border-border1 rounded">
      <span className="pl-3 pr-2 text-[10.5px] tracking-wider uppercase text-dim">Pump</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as PumpId)}
        className={clsx(
          "bg-transparent border-0 text-text text-[13px] pr-3 pl-1 py-1.5 outline-none cursor-pointer",
        )}
      >
        {PUMP_LIST.map((p) => {
          const ok = availableSet.has(p.id);
          return (
            <option key={p.id} value={p.id} disabled={!ok}>
              {p.displayName}{ok ? "" : "  (no data)"}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function RangeToggle({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {RANGES.map((r) => (
        <button
          key={r.id}
          onClick={() => onChange(r.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            range === r.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}
